import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  Scale,
  Copy,
  Edit,
  Trash2,
  Building2,
  Sparkles,
  RefreshCcw,
  Loader2,
  CalendarPlus,
  ArrowRight,
  Clock,
  Gavel,
  Wallet,
  User
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Case } from '@/types/case';
import { useToast } from '@/hooks/use-toast';

interface CaseListItemProps {
  case_: Case;
  onEdit: (case_: Case) => void;
  onDelete: (id: string) => void;
  onAnalyze: (case_: Case) => void;
  onSync: (id: string) => void;
  onAddEvent: (case_: Case) => void;
  isSyncing?: boolean;
  allClients: { id: string; name: string; email: string; }[];
}

const SMALL_WORDS = new Set(['de', 'da', 'do', 'das', 'dos', 'e', 'em', 'a', 'o', 'x', 'na', 'no']);
const KEEP_UPPER = new Set(['LTDA', 'LTDA.', 'S/A', 'SA', 'S.A.', 'ME', 'EPP', 'INSS', 'CEF', 'MEI', 'II', 'III', 'IV']);

// Títulos vindos do PJe chegam em CAIXA ALTA; converte para leitura mais confortável
const toReadableTitle = (title: string) => {
  const letters = title.replace(/[^A-Za-zÀ-ÿ]/g, '');
  if (!letters || letters !== letters.toUpperCase()) return title;
  return title
    .split(/\s+/)
    .map((word, index) => {
      if (KEEP_UPPER.has(word)) return word;
      const lower = word.toLowerCase();
      if (index > 0 && SMALL_WORDS.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
};

export const CaseListItem = ({ case_, onEdit, onDelete, onAnalyze, onSync, onAddEvent, isSyncing, allClients }: CaseListItemProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const isAdministrative = case_.process_type === 'administrative';

  const handleCardClick = () => {
    navigate(isAdministrative ? `/administrative-process/${case_.id}` : `/processo/${case_.id}`);
  };

  const getClientNames = () => {
    if (case_.is_litisconsorcio && case_.clients && case_.clients.length > 0) {
      const clientNames = Array.isArray(case_.clients) ? case_.clients.map(client => client.name) : [];
      return clientNames.join(', ');
    } else if (case_.client_id) {
      const client = allClients.find(client => client.id === case_.client_id);
      return client?.name || null;
    }
    return null;
  };

  const clientDisplay = getClientNames();

  const getStatusStyle = (status?: string) => {
    switch ((status || '').toLowerCase()) {
      case 'em andamento':
      case 'em-andamento': return { dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50' };
      case 'aguardando documentos':
      case 'aguardando-documentos': return { dot: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50' };
      case 'aguardando sentença': return { dot: 'bg-violet-500', text: 'text-violet-700', bg: 'bg-violet-50' };
      case 'suspenso': return { dot: 'bg-orange-500', text: 'text-orange-700', bg: 'bg-orange-50' };
      case 'transitado em julgado':
      case 'concluído':
      case 'arquivado': return { dot: 'bg-slate-400', text: 'text-slate-600', bg: 'bg-slate-100' };
      default: return { dot: 'bg-blue-500', text: 'text-blue-700', bg: 'bg-blue-50' };
    }
  };

  const statusLabel = (case_.status || 'Em andamento').replace(/-/g, ' ');
  const statusStyle = getStatusStyle(case_.status);
  const priority = (case_.priority || '').toLowerCase();
  const showPriority = priority === 'alta' || priority === 'urgente';

  const accentClass = isAdministrative ? 'bg-blue-500' : 'bg-[#c4ab83]';

  const calculateDaysUntilDeadline = () => {
    if (!case_.deadline) return null;
    const today = new Date();
    const deadline = new Date(case_.deadline);
    if (isNaN(deadline.getTime())) return null;
    const diffTime = deadline.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getDaysUntilDeadline = () => {
    const days = calculateDaysUntilDeadline();
    if (days === null) return null;
    if (days < 0) return { text: `Prazo vencido há ${Math.abs(days)} dia${Math.abs(days) === 1 ? '' : 's'}`, color: 'text-red-600 bg-red-50' };
    if (days === 0) return { text: 'Prazo vence hoje', color: 'text-red-600 bg-red-50' };
    if (days <= 7) return { text: `Prazo em ${days} dia${days === 1 ? '' : 's'}`, color: 'text-orange-700 bg-orange-50' };
    return { text: `Prazo em ${days} dias`, color: 'text-slate-600 bg-slate-100' };
  };

  const deadlineInfo = getDaysUntilDeadline();

  const formatCurrency = (value?: number | string) => {
    if (value === undefined || value === null || value === "") return null;
    const numberValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d,]/g, '').replace(',', '.')) : value;
    if (isNaN(numberValue)) return null;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(numberValue);
  };

  const formatCNJNumber = (number?: string) => {
    if (!number) return null;
    const cleanNumber = number.replace(/\D/g, '');
    if (cleanNumber.length >= 20) {
      const sequencial = cleanNumber.substring(0, 7);
      const digito = cleanNumber.substring(7, 9);
      const ano = cleanNumber.substring(9, 13);
      const segmento = cleanNumber.substring(13, 14);
      const tribunal = cleanNumber.substring(14, 16);
      const origem = cleanNumber.substring(16, 20);
      return `${sequencial}-${digito}.${ano}.${segmento}.${tribunal}.${origem}`;
    }
    return number;
  };

  const processNumber = isAdministrative ? case_.process_number : formatCNJNumber(case_.case_number_cnj);

  const copyNumber = async () => {
    if (!processNumber) return;
    try {
      await navigator.clipboard.writeText(processNumber);
      toast({ title: 'Número copiado', description: processNumber });
    } catch {
      // área de transferência indisponível
    }
  };

  // Movimentação nova ainda não vista (lida pelos e-mails dos tribunais)
  const movementAt = (case_ as any).movement_update_at as string | null;
  const seenAt = (case_ as any).movement_seen_at as string | null;
  const hasNewMovement = !!movementAt && (!seenAt || new Date(seenAt) < new Date(movementAt));
  const isImportantMovement = hasNewMovement && !!(case_ as any).movement_important;

  const caseValue = formatCurrency(case_.case_value);
  const clientFee = formatCurrency(case_.client_fee);

  return (
    <Card className="group relative max-w-full overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-none transition-all duration-300 hover:-translate-y-px hover:border-[#c4ab83]/60 hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.18)]">
      <div className={`absolute inset-y-0 left-0 w-1 ${accentClass}`} />

      <CardContent className="box-border max-w-full p-4 pl-5">
        {/* Cabeçalho: tipo, número, título e situação */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-500">
              {hasNewMovement && (
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] ${isImportantMovement ? 'bg-red-600 text-white' : 'bg-[#c4ab83] text-slate-900'}`}
                  title={(case_ as any).last_movement_summary || 'Movimentação nova'}
                >
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                  </span>
                  {isImportantMovement ? 'Decisão ou intimação' : 'Movimentação nova'}
                </span>
              )}
              <span className={`inline-flex items-center gap-1 font-semibold uppercase tracking-[0.12em] ${isAdministrative ? 'text-blue-600' : 'text-[#9a7f52]'}`}>
                {isAdministrative ? <Building2 className="h-3 w-3" /> : <Scale className="h-3 w-3" />}
                {isAdministrative ? 'Administrativo' : 'Judicial'}
              </span>
              {processNumber && (
                <>
                  <span className="text-slate-300">•</span>
                  <button
                    type="button"
                    onClick={copyNumber}
                    title="Copiar número do processo"
                    className="inline-flex items-center gap-1 font-mono text-[15px] font-semibold tracking-tight text-slate-700 transition hover:text-slate-900"
                  >
                    {processNumber}
                    <Copy className="h-4 w-4 opacity-0 transition group-hover:opacity-100" />
                  </button>
                </>
              )}
            </div>
            <button type="button" onClick={handleCardClick} className="block max-w-full text-left">
              <h3 className="line-clamp-2 text-base font-semibold leading-snug text-slate-900 transition-colors hover:text-[#9a7f52]">
                {case_.title || 'PROCESSO SEM TÍTULO'}
              </h3>
            </button>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-1.5 sm:justify-end">
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium capitalize ${statusStyle.bg} ${statusStyle.text}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${statusStyle.dot}`} />
              {statusLabel}
            </span>
            {showPriority && (
              <Badge className="rounded-full bg-red-500 px-2.5 py-0.5 text-[11px] font-medium text-white hover:bg-red-500">
                Prioridade {case_.priority?.toLowerCase()}
              </Badge>
            )}
            {case_.is_litisconsorcio && (
              <Badge variant="outline" className="rounded-full px-2.5 py-0.5 text-[11px] font-medium text-blue-700">
                Litisconsórcio
              </Badge>
            )}
          </div>
        </div>

        {/* Rodapé: cliente, prazos, valores e ações */}
        <div className="mt-3 flex flex-col gap-2 border-t border-slate-100 pt-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-[11px]">
            <span
              className={`inline-flex min-w-0 max-w-full items-center gap-1.5 text-[13px] ${clientDisplay ? 'font-medium text-slate-700' : 'italic text-slate-400'}`}
              title={clientDisplay || undefined}
            >
              <User className="h-3.5 w-3.5 shrink-0 text-slate-400" />
              <span className="truncate">{clientDisplay || 'Cliente não vinculado'}</span>
            </span>
            {deadlineInfo && (
              <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 font-semibold ${deadlineInfo.color}`}>
                <Clock className="h-3 w-3" /> {deadlineInfo.text}
              </span>
            )}
            {case_.next_hearing && (
              <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 font-semibold text-blue-700">
                <Gavel className="h-3 w-3" /> Audiência {format(new Date(case_.next_hearing), 'dd/MM/yyyy', { locale: ptBR })}
              </span>
            )}
            {(caseValue || clientFee) && (
              <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">
                <Wallet className="h-3 w-3" />
                {caseValue && `Causa ${caseValue}`}
                {caseValue && clientFee && ' · '}
                {clientFee && `Honorários ${clientFee}`}
              </span>
            )}
          </div>

          <div className="flex items-center justify-end gap-0.5">
            <div className="flex items-center gap-0.5 transition-opacity lg:opacity-40 lg:group-hover:opacity-100">
              <Button variant="ghost" size="icon" onClick={() => onAddEvent(case_)} className="h-8 w-8 text-slate-500 hover:bg-blue-50 hover:text-blue-600" title="Adicionar prazo">
                <CalendarPlus className="h-4 w-4" />
              </Button>
              {case_.datajud_monitorando &&
                (isSyncing ? (
                  <div className="flex h-8 w-8 items-center justify-center">
                    <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                  </div>
                ) : (
                  <Button variant="ghost" size="icon" onClick={() => onSync(case_.id)} className="h-8 w-8 text-slate-500 hover:bg-blue-50 hover:text-blue-600" title="Sincronizar com DataJud">
                    <RefreshCcw className="h-4 w-4" />
                  </Button>
                ))}
              <Button variant="ghost" size="icon" onClick={() => onAnalyze(case_)} className="h-8 w-8 text-slate-500 hover:bg-purple-50 hover:text-purple-600" title="Análise com IA">
                <Sparkles className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => onEdit(case_)} className="h-8 w-8 text-slate-500 hover:bg-amber-50 hover:text-amber-700" title="Editar">
                <Edit className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => onDelete(case_.id)} className="h-8 w-8 text-slate-500 hover:bg-red-50 hover:text-red-600" title="Excluir">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleCardClick}
              className="ml-2 h-8 rounded-lg border-[#c4ab83] px-3 text-xs font-semibold text-[#7a6440] hover:bg-[#c4ab83]/10 hover:text-[#5c4a2e]"
            >
              Abrir processo
              <ArrowRight className="ml-1 h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
